import React, { useState } from 'react';
import { Button, Container, Grid, Box, Tooltip, Typography, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLoaderData } from 'react-router-dom';
import { AssignmentTurnedIn, AutoAwesome, Close } from '@mui/icons-material';
import { 
    SkillCategory, 
    SkillTag, 
    SkillsContainer, 
    SkillsSection,
    SectionTitle,
    CategoryTitle 
} from '../styles/skills.styles';
import { sparkleStyles } from '../styles/common.styles';
import CommonDialog from '../reusable-components/common-dialog';
import { summarizeContent } from '../utils/summarizeContent';
import createSliderStyles from '../styles/slider.styles';

const SkillsShowcase = () => {
    const theme = useTheme();
    const { meInfo } = useLoaderData();
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryResult, setSummaryResult] = useState('');
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [multipleCertsOpen, setMultipleCertsOpen] = useState(false);
    const [selectedSkill, setSelectedSkill] = useState('');
    const [selectedCertifications, setSelectedCertifications] = useState([]);
    const styles = createSliderStyles(theme);

    const skillCategories = [
        {
            title: 'Programming Languages',
            skills: meInfo.skills.programmingLanguages,
            color: theme.palette.mode === 'dark' ? '#61dafb' : '#0288d1'  // Blue
        },
        {
            title: 'Frameworks',
            skills: meInfo.skills.frameworks,
            color: theme.palette.mode === 'dark' ? '#00d8ff' : '#0097a7'  // Cyan
        },
        {
            title: 'Cloud & Infrastructure',
            skills: meInfo.skills.cloudTechnologies,
            color: theme.palette.mode === 'dark' ? '#ff9900' : '#ed6c02'  // Orange
        },
        {
            title: 'Databases',
            skills: meInfo.skills.databases,
            color: theme.palette.mode === 'dark' ? '#4db33d' : '#2e7d32'  // Green
        },
    ];

    const handleSummarize = async () => {
        setSummaryLoading(true);
        try {
            const skillsData = skillCategories.map(category => ({
                category: category.title,
                skills: category.skills.join(', ')
            }));
            
            const result = await summarizeContent(
                `Can you analyze and provide a professional summary of divvi satish technical expertise based on these skills:
                ${skillsData.map(data => `${data.category}: ${data.skills}`).join('\n')}
                Focus on how these skills complement each other and what kind of roles or projects I would be well-suited for.`
            );
            setSummaryResult(result);
            setSummaryOpen(true);
        } catch (error) {
            console.error('Error getting summary:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    // Find all certifications for a skill
    const findCertifications = (skill) => {
        return meInfo.skills.certifications.filter(cert => cert.skillName === skill);
    };

    const handleMultipleCertsOpen = (skill, certifications) => {
        setSelectedSkill(skill);
        setSelectedCertifications(certifications);
        setMultipleCertsOpen(true);
    };

    const handleMultipleCertsClose = () => {
        setMultipleCertsOpen(false);
        setSelectedSkill('');
        setSelectedCertifications([]);
    };

    return (
        <SkillsSection id="skills" component="section">
            <Container>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: 2,
                    mb: 4
                }}>
                    <SectionTitle variant="h3" gutterBottom sx={styles.titleContainer}>
                        Skills Showcase
                        <Tooltip title="AI Analysis of Skills">
                        <Button 
                            onClick={handleSummarize} 
                            disabled={summaryLoading}
                            sx={{
                                minWidth: 'auto',
                                p: 1
                            }}
                        >
                            {summaryLoading ? <AutoAwesome sx={sparkleStyles} /> : <AutoAwesome />}
                        </Button>
                    </Tooltip>
                    </SectionTitle>
                </Box>

                <Grid container spacing={4}>
                    {skillCategories.map((category, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <SkillCategory 
                                elevation={0}
                                sx={{
                                    animation: `fadeInUp ${0.3 + index * 0.1}s ease-out`
                                }}
                            >
                                <CategoryTitle
                                    variant="h6"
                                    sx={{
                                        color: category.color,
                                        '&:after': {
                                            background: category.color,
                                        }
                                    }}
                                >
                                    {category.title}
                                </CategoryTitle>
                                <SkillsContainer>
                                    {category.skills.map((skill, skillIndex) => {
                                        const certifications = findCertifications(skill);
                                        return (
                                            <SkillTag
                                                key={skillIndex}
                                                sx={{
                                                    animation: `fadeIn ${0.3 + skillIndex * 0.1}s ease-out`,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                {skill}
                                                {certifications.length === 1 && (
                                                    <Tooltip
                                                        title={
                                                            <Box
                                                                sx={{
                                                                    textAlign: 'center',
                                                                    bgcolor:
                                                                        theme.palette.mode === 'dark'
                                                                            ? 'rgba(33,33,33,0.95)'
                                                                            : 'rgba(255,255,255,0.98)',
                                                                    p: 2,
                                                                    borderRadius: 1,
                                                                    color: theme.palette.text.primary,
                                                                    boxShadow: 3,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        mb: 1,
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            color: theme.palette.text.primary,
                                                                            mb: 0,
                                                                        }}
                                                                    >
                                                                        {certifications[0].title}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        color: theme.palette.text.secondary,
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    Issued by: {certifications[0].issuer}
                                                                </Typography>
                                                                <Box
                                                                    component="img"
                                                                    src={certifications[0].badgeUrl}
                                                                    alt={certifications[0].name}
                                                                    sx={{
                                                                        width: 150,
                                                                        height: 150,
                                                                        objectFit: 'contain',
                                                                        display: 'block',
                                                                        margin: '0 auto',
                                                                    }}
                                                                />
                                                                {certifications[0].verify && (
                                                                        <Button 
                                                                        variant="outlined" 
                                                                        color="secondary"
                                                                        target="_blank"
                                                                        href={certifications[0].verify}
                                                                        sx={{ mt: 2 }}
                                                                      >
                                                                        <AssignmentTurnedIn />
                                                                      </Button>
                                                                    )}
                                                            </Box>
                                                        }
                                                        arrow
                                                        placement="top"
                                                        sx={{
                                                            '& .MuiTooltip-tooltip': {
                                                                maxWidth: 'none',
                                                                bgcolor:
                                                                    theme.palette.mode === 'dark'
                                                                        ? 'rgba(33,33,33,0.95)'
                                                                        : 'rgba(255,255,255,0.98)',
                                                                color: theme.palette.text.primary,
                                                                boxShadow: 3,
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={certifications[0].badgeUrl}
                                                            alt={certifications[0].name}
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                objectFit: 'contain',
                                                                cursor: 'help',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {certifications.length > 1 && (
                                                    <Tooltip title="Click to view all certifications for this skill">
                                                        <Box
                                                            component="img"
                                                            src={certifications[0].badgeUrl}
                                                            alt={certifications[0].name}
                                                            onClick={() => handleMultipleCertsOpen(skill, certifications)}
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                objectFit: 'contain',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    transform: 'scale(1.1)',
                                                                    transition: 'transform 0.2s ease-in-out'
                                                                }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </SkillTag>
                                        );
                                    })}
                                </SkillsContainer>
                            </SkillCategory>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    width: '100%',
                    mt: 6 
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => {
                            document.getElementById('awards').scrollIntoView({ 
                                behavior: 'smooth',
                                block: 'start'
                            });
                        }}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            }
                        }}
                    >
                        AWARDS & RECOGNITION
                    </Button>
                </Box>
            </Container>

            <CommonDialog
                open={summaryOpen}
                onClose={() => setSummaryOpen(false)}
                title="AI Skills Analysis"
                content={summaryResult}
                isMarkdown
                showTTSButton={true}
            />

            <Dialog
                open={multipleCertsOpen}
                onClose={handleMultipleCertsClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        bgcolor: theme.palette.mode === 'dark' 
                            ? 'rgba(33,33,33,0.95)' 
                            : 'rgba(255,255,255,0.98)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: 3,
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 2,
                        mb: 3
                    }}
                >
                    <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                        Certifications for {selectedSkill}
                    </Typography>
                    <IconButton
                        onClick={handleMultipleCertsClose}
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                color: theme.palette.text.primary,
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Grid container spacing={3}>
                        {selectedCertifications.map((cert, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        p: 2,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        bgcolor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255,255,255,0.05)' 
                                            : 'rgba(0,0,0,0.02)',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2,
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: theme.palette.text.primary,
                                            mb: 1,
                                        }}
                                    >
                                        {cert.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            mb: 2,
                                        }}
                                    >
                                        Issued by: {cert.issuer}
                                    </Typography>
                                    <Box
                                        component="img"
                                        src={cert.badgeUrl}
                                        alt={cert.name}
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            objectFit: 'contain',
                                            display: 'block',
                                            margin: '0 auto',
                                            mb: 2,
                                        }}
                                    />
                                    {cert.verify && (
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            target="_blank"
                                            href={cert.verify}
                                            startIcon={<AssignmentTurnedIn />}
                                            size="small"
                                        >
                                            Verify
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
            </Dialog>
        </SkillsSection>
    );
};

export default SkillsShowcase; 